import * as THREE from 'three';

import { NODE_STRIDE, buildBvh, type Boxed, type Bvh } from './Bvh';

/**
 * The scene, flattened into the buffers a compute shader can read.
 *
 * **Two levels, and that is the whole design.** Every distinct geometry becomes
 * one BVH over its own triangles, in its own object space (a *BLAS*); every
 * mesh drawn with that geometry becomes an instance carrying a matrix (the
 * *TLAS* is a BVH over those). A pine tree is therefore stored once and pointed
 * at two thousand times. Unrolling instead — writing every instance's triangles
 * out in world space — is 2,1 million triangles for the props alone, which is
 * both too slow to build while somebody is looking and too big to keep.
 *
 * The cost is one extra indirection in the shader: at a TLAS leaf the ray is
 * transformed into the instance's object space before the BLAS is walked, and
 * the hit is transformed back. That is the standard arrangement and it is why
 * a GPU with hardware ray tracing exposes exactly these two levels.
 */

/** Floats per instance record; see `writeInstance`. */
const INSTANCE_STRIDE = 40;

/** What a hit surface does with light. */
export const MATERIAL_DIFFUSE = 0;
export const MATERIAL_WATER = 1;

export interface SceneBuffers {
  /** Vertex positions, three floats each, every BLAS one after another. */
  vertices: Float32Array;
  normals: Float32Array;
  /** Vertex colours, or white where the geometry carries none. */
  colors: Float32Array;
  /** Triangle corners, three per triangle, **relative to the BLAS's vertices**. */
  indices: Uint32Array;
  blasNodes: Float32Array;
  blasOrder: Uint32Array;
  tlasNodes: Float32Array;
  tlasOrder: Uint32Array;
  instances: Float32Array;
  instanceCount: number;
  /** For the read-out, not for the shader. */
  stats: { geometries: number; instances: number; triangles: number };
}

interface Blas {
  bvh: Bvh;
  nodeBase: number;
  orderBase: number;
  indexBase: number;
  vertexBase: number;
  triangles: number;
}

interface Instance {
  blas: Blas;
  matrix: THREE.Matrix4;
  material: number;
  tint: THREE.Color;
  /** World-space box, for the TLAS to sort by. */
  box: THREE.Box3;
}

/**
 * Walk a scene and take everything drawable out of it.
 *
 * `isWater` decides which meshes get the reflective material. Passed in rather
 * than read off a name or a material type, because the caller is the thing that
 * created those materials and knows which is which — guessing from a name would
 * be a second source of truth about the same meshes.
 */
export function captureScene(
  root: THREE.Object3D,
  isWater: (mesh: THREE.Mesh) => boolean
): SceneBuffers {
  const byGeometry = new Map<string, Blas>();
  const instances: Instance[] = [];

  const vertices: Float32Array[] = [];
  const normals: Float32Array[] = [];
  const colors: Float32Array[] = [];
  const indices: Uint32Array[] = [];
  const blasNodes: Float32Array[] = [];
  const blasOrder: Uint32Array[] = [];

  let vertexBase = 0;
  let indexBase = 0;
  let nodeBase = 0;
  let orderBase = 0;
  let triangleTotal = 0;

  const blasFor = (geometry: THREE.BufferGeometry): Blas | null => {
    const held = byGeometry.get(geometry.uuid);
    if (held) return held;

    const position = geometry.getAttribute('position');
    const index = geometry.getIndex();
    if (!position || !index) return null;

    const triangles = index.count / 3;
    if (triangles < 1) return null;

    const count = position.count;
    const localVertices = new Float32Array(count * 3);
    const localNormals = new Float32Array(count * 3);
    const localColors = new Float32Array(count * 3).fill(1);

    const normal = geometry.getAttribute('normal');
    const color = geometry.getAttribute('color');
    for (let i = 0; i < count; i++) {
      localVertices[i * 3] = position.getX(i);
      localVertices[i * 3 + 1] = position.getY(i);
      localVertices[i * 3 + 2] = position.getZ(i);

      if (normal) {
        localNormals[i * 3] = normal.getX(i);
        localNormals[i * 3 + 1] = normal.getY(i);
        localNormals[i * 3 + 2] = normal.getZ(i);
      }
      if (color) {
        localColors[i * 3] = color.getX(i);
        localColors[i * 3 + 1] = color.getY(i);
        localColors[i * 3 + 2] = color.getZ(i);
      }
    }

    const localIndices = new Uint32Array(index.count);
    for (let i = 0; i < index.count; i++) localIndices[i] = index.getX(i);

    const items: Boxed = {
      count: triangles,
      bounds(triangle, out) {
        out[0] = out[1] = out[2] = Infinity;
        out[3] = out[4] = out[5] = -Infinity;
        for (let corner = 0; corner < 3; corner++) {
          const at = localIndices[triangle * 3 + corner] * 3;
          for (let axis = 0; axis < 3; axis++) {
            out[axis] = Math.min(out[axis], localVertices[at + axis]);
            out[3 + axis] = Math.max(out[3 + axis], localVertices[at + axis]);
          }
        }
      },
    };

    const built: Blas = {
      bvh: buildBvh(items),
      nodeBase,
      orderBase,
      indexBase,
      vertexBase,
      triangles,
    };

    vertices.push(localVertices);
    normals.push(localNormals);
    colors.push(localColors);
    indices.push(localIndices);
    blasNodes.push(built.bvh.nodes);
    blasOrder.push(built.bvh.order);

    vertexBase += count;
    indexBase += index.count;
    nodeBase += built.bvh.nodeCount;
    orderBase += built.bvh.order.length;
    triangleTotal += triangles;

    byGeometry.set(geometry.uuid, built);
    return built;
  };

  const white = new THREE.Color(1, 1, 1);
  const matrix = new THREE.Matrix4();

  root.updateMatrixWorld(true);
  root.traverseVisible((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;

    const blas = blasFor(mesh.geometry as THREE.BufferGeometry);
    if (!blas) return;

    const material = mesh.material as THREE.MeshStandardMaterial | undefined;
    // A geometry that carries its own colours is already coloured; the tint
    // then has to be white or every surface would be dimmed twice. Where it
    // does not, the tint is the whole colour — which is how two buildings of
    // the same shape come out in their owners' colours off one BLAS.
    const tinted = !(mesh.geometry as THREE.BufferGeometry).getAttribute('color');
    const tint = tinted && material?.color ? material.color : white;

    const instanced = mesh as THREE.InstancedMesh;
    const many = instanced.isInstancedMesh ? instanced.count : 0;
    const box = new THREE.Box3().setFromBufferAttribute(
      mesh.geometry.getAttribute('position') as THREE.BufferAttribute
    );

    if (many > 0) {
      for (let i = 0; i < many; i++) {
        instanced.getMatrixAt(i, matrix);
        const world = new THREE.Matrix4().multiplyMatrices(instanced.matrixWorld, matrix);
        instances.push({
          blas,
          matrix: world,
          material: MATERIAL_DIFFUSE,
          tint: tint.clone(),
          box: box.clone().applyMatrix4(world),
        });
      }
      return;
    }

    instances.push({
      blas,
      matrix: mesh.matrixWorld.clone(),
      material: isWater(mesh) ? MATERIAL_WATER : MATERIAL_DIFFUSE,
      tint: tint.clone(),
      box: box.clone().applyMatrix4(mesh.matrixWorld),
    });
  });

  const tlas = buildBvh({
    count: instances.length,
    bounds(i, out) {
      const { min, max } = instances[i].box;
      out[0] = min.x;
      out[1] = min.y;
      out[2] = min.z;
      out[3] = max.x;
      out[4] = max.y;
      out[5] = max.z;
    },
  });

  const records = new Float32Array(Math.max(1, instances.length) * INSTANCE_STRIDE);
  instances.forEach((instance, i) => writeInstance(records, i, instance));

  return {
    vertices: concat(vertices, Float32Array),
    normals: concat(normals, Float32Array),
    colors: concat(colors, Float32Array),
    indices: concat(indices, Uint32Array),
    blasNodes: concat(blasNodes, Float32Array),
    blasOrder: concat(blasOrder, Uint32Array),
    tlasNodes: tlas.nodes,
    tlasOrder: tlas.order,
    instances: records,
    instanceCount: instances.length,
    stats: {
      geometries: byGeometry.size,
      instances: instances.length,
      triangles: triangleTotal,
    },
  };
}

/**
 * One instance, as forty floats.
 *
 * ```text
 *   0..15   world → object, for putting the ray into the geometry's own space
 *   16..27  object → world upper 3×3, three padded rows, for the normal
 *   28..31  node base, order base, index base, vertex base (bit-cast to u32)
 *   32..35  tint
 *   36      material
 *   37..39  padding to a multiple of four, which WGSL insists on
 * ```
 *
 * The bases are written with a bit-cast rather than as floats because a
 * quarter-million-vertex offset is exact in `u32` and not in `f32`; a scene
 * bigger than sixteen million vertices would otherwise start reading the wrong
 * triangles, and it would do it silently.
 */
function writeInstance(out: Float32Array, at: number, instance: Instance): void {
  const slot = at * INSTANCE_STRIDE;
  const inverse = new THREE.Matrix4().copy(instance.matrix).invert();

  // Three.js keeps matrices column-major in `elements`, which is the order WGSL
  // reads a mat4x4 in, so this is a straight copy rather than a transpose.
  for (let i = 0; i < 16; i++) out[slot + i] = inverse.elements[i];

  const m = instance.matrix.elements;
  const rows = [
    [m[0], m[1], m[2]],
    [m[4], m[5], m[6]],
    [m[8], m[9], m[10]],
  ];
  rows.forEach((row, i) => {
    out[slot + 16 + i * 4] = row[0];
    out[slot + 16 + i * 4 + 1] = row[1];
    out[slot + 16 + i * 4 + 2] = row[2];
    out[slot + 16 + i * 4 + 3] = 0;
  });

  const bases = new Uint32Array(out.buffer, out.byteOffset + (slot + 28) * 4, 4);
  bases[0] = instance.blas.nodeBase;
  bases[1] = instance.blas.orderBase;
  bases[2] = instance.blas.indexBase;
  bases[3] = instance.blas.vertexBase;

  out[slot + 32] = instance.tint.r;
  out[slot + 33] = instance.tint.g;
  out[slot + 34] = instance.tint.b;
  out[slot + 35] = 1;
  out[slot + 36] = instance.material;
}

/** The buffers as the GPU sees them, and the offsets that unpack them. */
export interface PackedScene {
  /** Nine floats per vertex: position, normal, colour. */
  geometry: Float32Array;
  /** Geometry-level nodes, then instance-level ones. */
  nodes: Float32Array;
  /** Geometry leaf order, instance leaf order, then triangle corners. */
  ints: Uint32Array;
  instances: Float32Array;
  /** tlas node base, tlas order base, index base. */
  bases: [number, number, number];
}

/**
 * Squeeze the capture into the five buffers the shader binds.
 *
 * **The packing is here rather than in the host** so that the same function
 * feeds the GPU and the test: every offset in this file is one the shader
 * computes back out with plain arithmetic, and an off-by-one in any of them is
 * a picture that is wrong rather than a picture that fails. Verified against
 * brute force over the same scene.
 *
 * Why one buffer where there were four: WebGPU guarantees only **eight storage
 * buffers per shader stage**, and the natural layout wants ten.
 */
export function packScene(captured: SceneBuffers): PackedScene {
  const vertices = captured.vertices.length / 3;
  const geometry = new Float32Array(Math.max(9, vertices * 9));
  for (let i = 0; i < vertices; i++) {
    geometry[i * 9] = captured.vertices[i * 3];
    geometry[i * 9 + 1] = captured.vertices[i * 3 + 1];
    geometry[i * 9 + 2] = captured.vertices[i * 3 + 2];
    geometry[i * 9 + 3] = captured.normals[i * 3];
    geometry[i * 9 + 4] = captured.normals[i * 3 + 1];
    geometry[i * 9 + 5] = captured.normals[i * 3 + 2];
    geometry[i * 9 + 6] = captured.colors[i * 3];
    geometry[i * 9 + 7] = captured.colors[i * 3 + 1];
    geometry[i * 9 + 8] = captured.colors[i * 3 + 2];
  }

  const nodes = new Float32Array(captured.blasNodes.length + captured.tlasNodes.length);
  nodes.set(captured.blasNodes, 0);
  nodes.set(captured.tlasNodes, captured.blasNodes.length);

  const ints = new Uint32Array(
    captured.blasOrder.length + captured.tlasOrder.length + captured.indices.length
  );
  ints.set(captured.blasOrder, 0);
  ints.set(captured.tlasOrder, captured.blasOrder.length);
  ints.set(captured.indices, captured.blasOrder.length + captured.tlasOrder.length);

  return {
    geometry,
    nodes,
    ints,
    instances: captured.instances,
    bases: [
      captured.blasNodes.length / NODE_STRIDE,
      captured.blasOrder.length,
      captured.blasOrder.length + captured.tlasOrder.length,
    ],
  };
}

function concat<T extends Float32Array | Uint32Array>(
  parts: T[],
  kind: { new (length: number): T }
): T {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new kind(Math.max(1, total));
  let at = 0;
  for (const part of parts) {
    out.set(part as never, at);
    at += part.length;
  }
  return out;
}

export { INSTANCE_STRIDE, NODE_STRIDE };
